import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, Table2, Plus, X, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DataTablesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', application_table: 'clients', description: '', is_multi_row: false, columns: [] });
  const [newCol, setNewCol] = useState({ name: '', type: 'string' });
  const [rowsModal, setRowsModal] = useState(null); // {table, rows}
  const [newRow, setNewRow] = useState({ entity_id: '', values: {} });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/data-tables'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const addColumn = () => {
    if (!newCol.name) return;
    setForm({ ...form, columns: [...form.columns, { ...newCol }] });
    setNewCol({ name: '', type: 'string' });
  };

  const removeColumn = (idx) => {
    setForm({ ...form, columns: form.columns.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Table name is required'); return; }
    try { await api.post('/data-tables', form); toast.success('Data table created'); setShowModal(false); setForm({ name: '', application_table: 'clients', description: '', is_multi_row: false, columns: [] }); load(); }
    catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this data table?')) return;
    try { await api.delete(`/data-tables/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const openRows = async (table) => {
    try {
      const res = await api.get(`/data-tables/${table.id}/rows`);
      setRowsModal({ table: res.data.table, rows: res.data.rows });
      setNewRow({ entity_id: '', values: {} });
    } catch { toast.error('Failed to load rows'); }
  };

  const reloadRows = async () => {
    if (!rowsModal) return;
    const res = await api.get(`/data-tables/${rowsModal.table.id}/rows`);
    setRowsModal({ ...rowsModal, rows: res.data.rows });
  };

  const handleAddRow = async () => {
    if (!newRow.entity_id) { toast.error(`${rowsModal.table.application_table.replace('_', ' ')} ID is required`); return; }
    try {
      await api.post(`/data-tables/${rowsModal.table.id}/rows`, newRow);
      toast.success('Row added');
      setNewRow({ entity_id: '', values: {} });
      reloadRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Delete this row?')) return;
    try { await api.delete(`/data-tables/${rowsModal.table.id}/rows/${rowId}`); reloadRows(); }
    catch { toast.error('Failed'); }
  };

  return (
    <AdminSubPage title="Manage Data Tables" breadcrumb={['Admin', 'System', 'Data Tables']} backPath="/dashboard/admin/system" actionLabel="+ Create Data Table" onAction={() => setShowModal(true)}>
      <div className="bg-white border border-gray-200" data-testid="data-tables-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Table Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Applies To</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Columns</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Multi-Row</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400"><Table2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />No data tables defined.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`dt-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div><div className="text-xs text-gray-400">{item.description || ''}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.application_table}</td>
                <td className="px-4 py-3 text-sm text-center">{(item.columns || []).length}</td>
                <td className="px-4 py-3 text-center"><span className={`text-xs ${item.is_multi_row ? 'text-emerald-600' : 'text-gray-400'}`}>{item.is_multi_row ? 'Yes' : 'No'}</span></td>
                <td className="px-4 py-3 flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => openRows(item)} data-testid={`view-rows-${item.id}`}><Database className="w-3 h-3 mr-1" />View Rows</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="dt-desc"><DialogHeader><DialogTitle>Create Data Table</DialogTitle></DialogHeader>
          <p id="dt-desc" className="sr-only">Data table form</p>
          <div className="space-y-4">
            <div><Label>Table Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="dt-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Application Table</Label>
                <Select value={form.application_table} onValueChange={v => setForm({...form, application_table: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clients">Clients</SelectItem>
                    <SelectItem value="loans">Loans</SelectItem>
                    <SelectItem value="savings_accounts">Savings Accounts</SelectItem>
                    <SelectItem value="offices">Offices</SelectItem>
                    <SelectItem value="groups">Groups</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Label>Multi-Row</Label>
                <Switch checked={form.is_multi_row} onCheckedChange={v => setForm({...form, is_multi_row: v})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Columns</Label>
              <div className="flex gap-2">
                <Input placeholder="Column name" value={newCol.name} onChange={e => setNewCol({...newCol, name: e.target.value})} className="flex-1" />
                <Select value={newCol.type} onValueChange={v => setNewCol({...newCol, type: v})}>
                  <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" size="sm" variant="outline" onClick={addColumn}><Plus className="w-4 h-4" /></Button>
              </div>
              {form.columns.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.columns.map((col, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-1.5 border">
                      <span className="flex-1 font-medium">{col.name}</span>
                      <span className="text-gray-400 text-xs capitalize">{col.type}</span>
                      <button onClick={() => removeColumn(i)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="dt-submit-btn">Create Table</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rows modal */}
      <Dialog open={!!rowsModal} onOpenChange={(o) => !o && setRowsModal(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="dt-rows-desc">
          <DialogHeader><DialogTitle>{rowsModal?.table?.name} — Rows</DialogTitle></DialogHeader>
          <p id="dt-rows-desc" className="sr-only">Manage rows</p>
          {rowsModal && (
            <div className="space-y-4">
              <div className="bg-gray-50 border p-3">
                <p className="text-xs text-gray-500 mb-2">Add a row {rowsModal.table.is_multi_row ? '(multi-row table — many per entity)' : '(single-row — only one per entity)'}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input placeholder={`${rowsModal.table.application_table || 'entity'} ID`} value={newRow.entity_id} onChange={e => setNewRow({ ...newRow, entity_id: e.target.value })} data-testid="dt-row-entity-id" />
                </div>
                {(rowsModal.table.columns || []).map((col, i) => (
                  <div key={i} className="mt-2 grid grid-cols-3 gap-2 items-center">
                    <Label className="text-xs">{col.name} <span className="text-gray-400">({col.type})</span></Label>
                    <Input className="col-span-2" value={newRow.values[col.name] || ''} onChange={e => setNewRow({ ...newRow, values: { ...newRow.values, [col.name]: e.target.value } })} data-testid={`dt-row-val-${col.name}`} />
                  </div>
                ))}
                <Button onClick={handleAddRow} className="bg-[#1f1f1f] text-white mt-3" data-testid="dt-row-add-btn"><Plus className="w-4 h-4 mr-1" />Add Row</Button>
              </div>

              <div className="border">
                <table className="w-full text-sm" data-testid="dt-rows-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs">Entity</th>
                      {(rowsModal.table.columns || []).map((c, i) => <th key={i} className="px-2 py-2 text-left text-xs">{c.name}</th>)}
                      <th className="px-2 py-2 text-left text-xs">Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rowsModal.rows.length === 0 && <tr><td colSpan={3 + (rowsModal.table.columns || []).length} className="px-2 py-6 text-center text-gray-400">No rows yet.</td></tr>}
                    {rowsModal.rows.map(r => (
                      <tr key={r.id} data-testid={`dt-row-rec-${r.id}`}>
                        <td className="px-2 py-2 font-mono text-xs">{r.entity_id}</td>
                        {(rowsModal.table.columns || []).map((c, i) => <td key={i} className="px-2 py-2">{String(r.values?.[c.name] ?? '-')}</td>)}
                        <td className="px-2 py-2 text-xs text-gray-500">{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-2 py-2"><button onClick={() => handleDeleteRow(r.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setRowsModal(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default DataTablesPage;
